import { Navigate, useLocation } from 'react-router-dom'
import { useSession } from '@/hooks/use-session'
import { useLocaleNavigate } from '@/hooks/use-locale-navigate'
import { Skeleton } from '@/components/ui/skeleton'

interface ProtectedRouteProps {
  children: React.ReactNode
  /** Where to redirect unauthenticated users. Defaults to /sign-in */
  redirectTo?: string
}

/**
 * Wraps routes that require authentication AND age confirmation.
 *
 * Gate order:
 *   1. Loading  → skeleton (never flash content)
 *   2. No user  → /sign-in?from=...
 *   3. No ageConfirmedAt → /age-confirm?from=... (OAuth users who skipped signup form)
 *   4. Pass     → render children
 */
export function ProtectedRoute({ children, redirectTo = '/sign-in' }: ProtectedRouteProps) {
  const { user, isLoading } = useSession()
  const location = useLocation()
  const { buildPath } = useLocaleNavigate()

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-4 md:px-8 py-16 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-72" />
        <Skeleton className="h-4 w-64" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to={`${buildPath(redirectTo)}?from=${encodeURIComponent(location.pathname)}`} replace />
  }

  if (!user.ageConfirmedAt) {
    return <Navigate to={`${buildPath('/age-confirm')}?from=${encodeURIComponent(location.pathname)}`} replace />
  }

  return <>{children}</>
}
